import { Position } from "../types";
import { pathExists, findAllReachablePositions } from "./pathfinding";
import { PLAYER_START_POSITION, getGrannyHousePosition } from "../constants/gameConfig";
import { positionsEqual } from "./gridUtils";

// ensures level is completable by verifying all objectives are reachable
export const validateLevel = (
  treePositions: Position[],
  flowerPositions: Position[],
  grannyHousePosition: Position,
  gridSize: number
): { isValid: boolean; reason?: string } => {

  if (!pathExists(PLAYER_START_POSITION, grannyHousePosition, treePositions, gridSize)) {
    return {
      isValid: false,
      reason: "House is not reachable from starting position",
    };
  }

  for (const flower of flowerPositions) {
    if (!pathExists(PLAYER_START_POSITION, flower, treePositions, gridSize)) {
      return {
        isValid: false,
        reason: `Flower at (${flower.x}, ${flower.y}) is not reachable from start`,
      };
    }
  }

  for (const flower of flowerPositions) {
    if (!pathExists(flower, grannyHousePosition, treePositions, gridSize)) {
      return {
        isValid: false,
        reason: `House is not reachable from flower at (${flower.x}, ${flower.y})`,
      };
    }
  }

  // verify all objectives are in one connected area using flood fill
  const reachableFromStart = findAllReachablePositions(PLAYER_START_POSITION, treePositions, gridSize);

  const posKey = (pos: Position) => `${pos.x},${pos.y}`;

  // make sure the house is reachable
  if (!reachableFromStart.has(posKey(grannyHousePosition))) {
    return {
      isValid: false,
      reason: "House is in a disconnected area",
    };
  }

  // make sure all flowers are reachable
  for (const flower of flowerPositions) {
    if (!reachableFromStart.has(posKey(flower))) {
      return {
        isValid: false,
        reason: `Flower at (${flower.x}, ${flower.y}) is in a disconnected area`,
      };
    }
  }

  return { isValid: true };
};

/**
 * checks if the player is stuck and can't reach remaining flowers or the house
 */
export const isPlayerStuck = (
  playerPosition: Position,
  remainingFlowers: Position[],
  treePositions: Position[],
  grannyHousePosition: Position,
  isHouseOpen: boolean,
  gridSize: number,
  collectedFlowers?: number,
  totalFlowers?: number
): { stuck: boolean; reason?: string } => {

  // if the house is open and we can reach it, we're good
  if (isHouseOpen) {
    if (pathExists(playerPosition, grannyHousePosition, treePositions, gridSize)) {
      return { stuck: false };
    } else {
      return {
        stuck: true,
        reason: "Cannot reach Granny's house from current position",
      };
    }
  }

  // if the house is still closed, check if we can reach any remaining flowers
  if (remainingFlowers.length === 0) {
    // if we have totalFlowers info, check if all required flowers have been collected
    // if so, the house should be open, so this isn't a stuck situation
    if (totalFlowers !== undefined && collectedFlowers !== undefined) {
      if (collectedFlowers >= totalFlowers) {
        // all required flowers collected - house should open, not stuck
        return { stuck: false };
      }
    }
    return {
      stuck: true,
      reason: "No flowers remaining but house is not open",
    };
  }

  // see if we can reach at least one flower
  for (const flower of remainingFlowers) {
    if (pathExists(playerPosition, flower, treePositions, gridSize)) {
      return { stuck: false };
    }
  }

  return {
    stuck: true,
    reason: "Cannot reach any remaining flowers from current position",
  };
};

